import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { UserITF } from '@/types'

/**
 * The backend feature flags, mirrored from
 * ActivateBackend/backend/enums/FeatureFlagEnums.js. Keep this union in sync
 * with the backend enum — a new flag on the backend should be added here too.
 */
export type FeatureName =
  | 'Feedback'
  | 'WorkOrders'
  | 'Inspections'
  | 'Equipment'
  | 'Analytics'
  | 'Communication'
  | 'Events'
  | 'QRCodes'
  | 'COI'
  | 'Vacancy'
  | 'Tenants'
  | 'Vendors'
  | 'PropertyManagement'
  | 'FrontDesk'
  | 'Purchases'
  | 'Scheduling'
  | 'Inventory'
  | 'MappingHub'
  | 'PcaHub'
  | 'Support'
  // Intelligence modules, toggled per portfolio in Account Info -> Analytics
  | 'EquipmentIntelligence'
  | 'LeaseIntelligence'
  | 'CapitalPlanning'
  | 'WorkOrderIntelligence'
  | 'RoofIntelligence'
  | 'CleaningIntelligence'
  | 'EngineeringIntelligence'
  | 'SecurityIntelligence'
  | 'PMIntelligence'
  | 'ComplianceIntelligence'

const EMPTY_FLAGS: Record<string, boolean> = {}

/**
 * The effective feature flags (global AND group) the backend computed for this
 * session. A missing key means disabled, exactly as Auth.js treats it, so what
 * the UI hides matches what the API would refuse. AcAdmin sessions get every
 * flag, mirroring their backend bypass.
 *
 * Refreshed by usePermissionRefresh when the Permissions-Version header moves,
 * so a toggle saved in Account Info -> Analytics lands within one request.
 */
export const useEnabledFeatures = (): Record<string, boolean> => {
  const user = useSelector((state: { profile: { user?: UserITF } }) => state.profile?.user)
  const flags = user?.UserContext?.EnabledFeatureFlags
  return useMemo(() => flags || EMPTY_FLAGS, [flags])
}

/**
 * True when the given feature is enabled for the current user's group.
 */
export const useFeature = (name: FeatureName): boolean => {
  const flags = useEnabledFeatures()
  return !!flags[name]
}
